/**
 * 目的锁定「已填内容会保留」暂存(2026-09-12 A3)。
 *
 * ═══ 治的是什么 ═══════════════════════════════════════════════════════════════
 * 目的一旦选定就锁死(进创建页不能换目的)。选错了唯一的出口是「回视频舱重选」,
 * 而那会把用户已经敲进「想拍什么」框的字、已经选好的素材全部丢掉 —— 于是用户不敢点,
 * 或者点了骂一句。所以离开前把这些暂存进 sessionStorage,回 hub 再选任一目的进来时回填。
 *
 * ═══ 为什么是 sessionStorage 而不是服务端草稿 ═══════════════════════════════════
 * 这是**编排前**的裸输入(还没花钱、还没产分镜),服务端草稿那套是编排后才落的。
 * 为一次「回去重选」动服务端草稿机制是杀鸡用牛刀(拍板:别动编排后的草稿机制)。
 * sessionStorage 的生命周期(本标签页会话)正好匹配这条路的时间尺度 ——
 * 走出去、逛一圈 hub、再进来,都在同一个会话里。
 *
 * ★只暂存**编排前的自由输入**:想拍什么正文 + 素材引用(产品实体 / 选品 / 选中的人物)。
 *   这些字段本来就随 formJson 往返草稿(可 JSON 序列化、可回灌),不是新造的搬运。
 * ★读/写全 try/catch:隐私模式 / 存储被禁 / 配额满时静默降级(不保留 ≠ 崩页)。
 * ★一次性消费:回填后立刻清,别让上一次的残留污染下一条不相干的片。
 */
import type { FormState } from "./videoCreateTypes";

const KEY = "video-intake-stash";

/**
 * 暂存的字段集 —— **只有编排前的自由输入**(想拍什么 + 素材引用)。
 * 目的 / 格式 / 时长这些「设定」不进:它们由新选的那张目的卡重新定,回填反而会盖掉新目的。
 */
export interface VideoIntakeStash {
  userInput: string;
  entities?: FormState["entities"];
  productRefKey?: FormState["productRefKey"];
  briefProducts?: FormState["briefProducts"];
  briefProductId?: FormState["briefProductId"];
  briefProductName?: FormState["briefProductName"];
  briefSellingPoints?: FormState["briefSellingPoints"];
  briefSellingPointsAll?: FormState["briefSellingPointsAll"];
  briefIncludeBrandFacts?: FormState["briefIncludeBrandFacts"];
  characterId?: FormState["characterId"];
  characterName?: FormState["characterName"];
  characterAppearance?: FormState["characterAppearance"];
  characterIdentityKeys?: FormState["characterIdentityKeys"];
  characterIsRealPerson?: FormState["characterIsRealPerson"];
  /**
   * ★0914 SEEDANCE 真人出镜 P0 追加(审查抓到的 P1 漏洞):这两个 verification 快照字段
   * 没跟着 characterId 三元组一起暂存/回填,「回 hub 重选目的再回来」这条路就会把真人的
   * verification 状态丢在半路 —— 人物 id/名字/appearance 都回填了,但两道闸(fail-closed)
   * 拿不到快照,把本可用的真人当"未认证"误拦,100% 复现。必须跟 characterId 系列同生共死。
   */
  characterVerificationStatus?: FormState["characterVerificationStatus"];
  characterVerificationAssets?: FormState["characterVerificationAssets"];
}

/** 从当前 form 抽出要暂存的那几样;全空(没写字也没选料)→ 不存,免得留个空壳去回填。 */
export function buildIntakeStash(form: FormState): VideoIntakeStash | null {
  const hasText = !!form.userInput?.trim();
  const hasEntities = (form.entities?.length ?? 0) > 0;
  const hasProducts = (form.briefProducts?.length ?? 0) > 0 || !!form.briefProductId;
  const hasCharacter = form.characterId != null;
  if (!hasText && !hasEntities && !hasProducts && !hasCharacter) return null;
  return {
    userInput: form.userInput ?? "",
    entities: form.entities,
    productRefKey: form.productRefKey,
    briefProducts: form.briefProducts,
    briefProductId: form.briefProductId,
    briefProductName: form.briefProductName,
    briefSellingPoints: form.briefSellingPoints,
    briefSellingPointsAll: form.briefSellingPointsAll,
    briefIncludeBrandFacts: form.briefIncludeBrandFacts,
    characterId: form.characterId,
    characterName: form.characterName,
    characterAppearance: form.characterAppearance,
    characterIdentityKeys: form.characterIdentityKeys,
    characterIsRealPerson: form.characterIsRealPerson,
    characterVerificationStatus: form.characterVerificationStatus,
    characterVerificationAssets: form.characterVerificationAssets,
  };
}

/** 暂存(离开创建页去 hub 前调)。写不进去静默 —— 保留是尽力而为,不是硬保证。 */
export function stashIntake(form: FormState): void {
  const stash = buildIntakeStash(form);
  try {
    if (stash) window.sessionStorage.setItem(KEY, JSON.stringify(stash));
    else window.sessionStorage.removeItem(KEY);
  } catch {
    /* 隐私模式 / 配额满:静默降级,不保留 */
  }
}

/** 读暂存(回创建页选目的时调);没有 / 坏了 → null。 */
export function readIntakeStash(): VideoIntakeStash | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const o = parsed as Record<string, unknown>;
    if (typeof o.userInput !== "string") return null;
    return o as unknown as VideoIntakeStash;
  } catch {
    return null;
  }
}

/** 一次性消费:回填后清掉。 */
export function clearIntakeStash(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* 静默 */
  }
}

/**
 * 把暂存叠回 form(在按目的种完 briefSeed 之后调,暂存里的字优先)。
 * ★想拍什么正文:暂存有非空文本就用它,盖掉刚种进去的 briefSeed(用户写过的字更重要)。
 * ★素材引用:暂存里存在(非 undefined)才回填,免得用空值抹掉目的档可能带的默认。
 */
export function applyIntakeStash(form: FormState, stash: VideoIntakeStash): FormState {
  const next: FormState = { ...form };
  if (stash.userInput?.trim()) next.userInput = stash.userInput;
  if (stash.entities !== undefined) next.entities = stash.entities;
  if (stash.productRefKey !== undefined) next.productRefKey = stash.productRefKey;
  if (stash.briefProducts !== undefined) next.briefProducts = stash.briefProducts;
  if (stash.briefProductId !== undefined) next.briefProductId = stash.briefProductId;
  if (stash.briefProductName !== undefined) next.briefProductName = stash.briefProductName;
  if (stash.briefSellingPoints !== undefined) next.briefSellingPoints = stash.briefSellingPoints;
  if (stash.briefSellingPointsAll !== undefined)
    next.briefSellingPointsAll = stash.briefSellingPointsAll;
  if (stash.briefIncludeBrandFacts !== undefined)
    next.briefIncludeBrandFacts = stash.briefIncludeBrandFacts;
  if (stash.characterId !== undefined) next.characterId = stash.characterId;
  if (stash.characterName !== undefined) next.characterName = stash.characterName;
  if (stash.characterAppearance !== undefined) next.characterAppearance = stash.characterAppearance;
  if (stash.characterIdentityKeys !== undefined)
    next.characterIdentityKeys = stash.characterIdentityKeys;
  if (stash.characterIsRealPerson !== undefined)
    next.characterIsRealPerson = stash.characterIsRealPerson;
  if (stash.characterVerificationStatus !== undefined)
    next.characterVerificationStatus = stash.characterVerificationStatus;
  if (stash.characterVerificationAssets !== undefined)
    next.characterVerificationAssets = stash.characterVerificationAssets;
  return next;
}
